import React, { type FunctionComponent } from 'react'
import { type TooltipFormatterContextObject } from 'highcharts'
import moment from 'moment-timezone'
import { colors, fontWeight } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { ArrowUpIcon, ArrowDownIcon } from '@instacart/ids-core'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { type TrendGraphTooltipFormatterContext } from 'domains/insights-portal/utils/trendGraphs.types'
import { useFormatValue } from 'domains/insights-portal/utils/format-value.hooks'
import { type TrendData } from 'domains/insights-portal/utils/trendGraphs.utils'
import {
  getSeriesPointsFromDataRefs,
  type transformMultiGroupDataToBenchmarkTrendData,
} from 'domains/insights-portal/widgets/KPIOverviewTrend/KpiOverviewTrend.utils'
import { InsightsPortalKpiOverviewTrendTooltip } from './InsightsPortalKpiOverviewTrendTooltip'

const ChangeContainer = styled.span(props => ({
  lineHeight: '14px',
  fontWeight: fontWeight.SEMIBOLD,
  marginBottom: 6,
  color: props.color,
  svg: {
    fill: props.color,
  },
}))

const KpiTooltipHeader = styled.div({
  fontWeight: 600,
  color: colors.SECONDARY.REGULAR,
})

const KpiTooltipContainer = styled.div({
  minWidth: '230px',
  display: 'flex',
  flexDirection: 'column',
})

const KpiTooltipRow = styled.div({
  minWidth: 230,
  display: 'flex',
  margin: '2px 0',
  justifyContent: 'space-between',
})

const KpiTooltipRowLabel = styled.span({
  fontSize: 10,
  color: colors.SECONDARY.REGULAR,
})

interface InsightsPortalComparisonTooltipProps {
  formatterContext: TooltipFormatterContextObject
  selectedMetricName: string
  primaryData: TrendData['primaryData']
  comparisonData: TrendData['comparisonData']
  benchmarkData?: ReturnType<typeof transformMultiGroupDataToBenchmarkTrendData>
  metricFormatter: (v: number) => string
}

export const InsightsPortalKpiComparisonTooltip: FunctionComponent<
  InsightsPortalComparisonTooltipProps
> = ({
  formatterContext,
  selectedMetricName,
  primaryData,
  comparisonData,
  benchmarkData,
  metricFormatter,
}) => {
  const messages = useDomainMessages({
    comparisonPeriod: 'insightsPortalDomain.outOfStockDashboard.compareToValue.comparisonPeriod',
    benchmark: 'insightsPortalDomain.benchmark',
    comparedPreviousPeriod:
      'insightsPortalDomain.performanceDashboard.tooltips.comparedToPreviousPeriod',
  })

  const { formatPercentage } = useFormatValue()

  const getDirectionIcon = (value: number) => {
    if (value === 0) return null

    return value < 0 ? (
      <ArrowDownIcon size={12} data-testid="metric-card-down-icon" />
    ) : (
      <ArrowUpIcon size={12} data-testid="metric-card-up-icon" />
    )
  }

  return (
    <InsightsPortalKpiOverviewTrendTooltip
      formatterContext={formatterContext as TrendGraphTooltipFormatterContext}
      header={() => <KpiTooltipHeader>{selectedMetricName}</KpiTooltipHeader>}
      body={(
        { points },
        primaryDataRef = primaryData,
        comparisonDataRef = comparisonData,
        benchmarkDataRef = benchmarkData
      ) => {
        const { primaryPoint, comparisonPoint, benchmarkPoint } = getSeriesPointsFromDataRefs(
          primaryDataRef,
          comparisonDataRef,
          benchmarkDataRef,
          points
        )

        const primaryDataPoint = primaryDataRef.find(dataPoint => dataPoint?.x === primaryPoint?.x)

        const comparisonDataPoint = comparisonDataRef.find(
          dataPoint => dataPoint?.x === comparisonPoint?.x
        )

        const benchmarkDataPoint =
          benchmarkDataRef &&
          benchmarkDataRef?.find(dataPoint => dataPoint?.x === benchmarkPoint?.x)

        return (
          <KpiTooltipContainer>
            {primaryPoint ? (
              <KpiTooltipRow key={`${primaryPoint?.x}${primaryPoint?.y}-percentage`}>
                <KpiTooltipRowLabel>{messages.comparedPreviousPeriod}</KpiTooltipRowLabel>
                <span
                  style={{ color: primaryPoint?.color.toString(), fontWeight: fontWeight.BOLD }}
                >
                  {
                    <ChangeContainer color={primaryPoint?.color.toString()}>
                      {getDirectionIcon(primaryDataPoint?.growthPercentage)}
                    </ChangeContainer>
                  }
                  {formatPercentage(primaryDataPoint?.growthPercentage)}
                </span>
              </KpiTooltipRow>
            ) : null}
            {primaryPoint ? (
              <KpiTooltipRow key={`${primaryPoint?.x}${primaryPoint?.y}-primary-abs-value`}>
                <KpiTooltipRowLabel>
                  {moment(primaryDataRef.find(data => data?.x === primaryPoint?.x)?.primaryDate)
                    .tz('utc')
                    .format('MMM, DD YYYY')}
                </KpiTooltipRowLabel>
                <span
                  style={{ color: primaryPoint?.color.toString(), fontWeight: fontWeight.BOLD }}
                >
                  {metricFormatter(primaryDataPoint?.y)}
                </span>
              </KpiTooltipRow>
            ) : null}
            {comparisonPoint ? (
              <KpiTooltipRow
                key={`${comparisonPoint?.x}${comparisonPoint?.y}-comparison-abs-value`}
              >
                <KpiTooltipRowLabel>
                  {moment(
                    comparisonDataRef.find(data => data?.x === comparisonPoint?.x)?.comparisonDate
                  )
                    .tz('utc')
                    .format('MMM, DD YYYY')}
                </KpiTooltipRowLabel>
                <span
                  style={{
                    color: comparisonPoint?.color.toString(),
                    fontWeight: fontWeight.BOLD,
                  }}
                >
                  {metricFormatter(comparisonDataPoint?.y)}
                </span>
              </KpiTooltipRow>
            ) : null}
            {benchmarkPoint ? (
              <KpiTooltipRow key={`${benchmarkPoint?.x}${benchmarkPoint?.y}-benchmark-abs-value`}>
                <KpiTooltipRowLabel>
                  {`${moment(
                    benchmarkDataRef.find(data => data?.x === benchmarkPoint.x)?.primaryDate
                  )
                    .tz('utc')
                    .format('MMM, DD YYYY')} ${messages.benchmark}`}
                </KpiTooltipRowLabel>
                <span
                  style={{
                    color: benchmarkPoint?.color.toString(),
                    fontWeight: fontWeight.BOLD,
                  }}
                >
                  {metricFormatter(benchmarkDataPoint?.y)}
                </span>
              </KpiTooltipRow>
            ) : null}
          </KpiTooltipContainer>
        )
      }}
    />
  )
}
